import {React, _, pluralize, observer, Highlighter, classNames, useAsObservableSource} from '@api'
import {Button, ButtonGroup, Icon, Menu, MenuDivider, Navbar, NavbarGroup} from '@blueprintjs/core'
import {Tooltip2} from '@api'
import {LoadingIndicator} from '@modules/common'
import {ui, GroupByGrouping, StoresInstance} from '@stores'
import {PrimaryIconButton, SearchField2} from 'centric-ui-components'
import {autorun, computed, observable, runInAction} from 'mobx'
import {Fragment, useContext} from 'react'
import {GroupByMenuItem, GroupByToolbarItems_MenuFooter} from '..'
import * as css from './GroupByDropdown.css'
import {useVipLocalStore, VipLocalStore} from '@modules'

/// region Store

export type GroupByDropDownMenuSource = 'toolbar' | 'contextMenu' | 'subtitleContextMenu'

export class GroupByDropdownStore extends VipLocalStore {
  static stub: Partial<GroupByDropdownStore> = {level: -1, source: 'contextMenu'}

  constructor(stores: StoresInstance, public props?: { level: number, group?: GroupByGrouping }, public source: GroupByDropDownMenuSource = 'toolbar') {
    super(stores)
  }

  @computed get isSubtitleSubmenu() {
    return this.source === 'subtitleContextMenu'
  }

  get grouping() {
    return this.props?.group
  }

  get level() {
    return this.props?.level ?? -1
  }

  /**
   * Probably dead code as this is done in a different component's store now
   * {@see GroupByToolbarChipsStore}
   */
  registerEffects = () => {
    this.hierarchy = observable.array<string>([], {deep: false})

    const {groupBy} = this
    const toDispose = [
      autorun(() => {
        groupBy.dropdownMenuStores.set(this.props?.level ?? 0, this)
      }),
      autorun(() => {
        const globalHierarchy = groupBy.hierarchy.slice()
        runInAction(() => {
          if (!_.isEqual(this.hierarchy.slice(), groupBy.hierarchy.slice())) {
            this.hierarchy.replace(groupBy.hierarchy.slice())
          }
        })
      }, {}),
      autorun(() => {
        const h = this.hierarchy.slice()
        if (!_.isEqual(h, groupBy.hierarchy.slice())) {
          this._debouncedUpdateHierarchyInGlobalStore()
        }
      })
    ]

    return () => toDispose.forEach(d => d())
  }

  @computed get isCatalogItemDropdown() {
    const {props: {level, group}} = this

    const isCatalogItemDropdown = level === -1 || group?.isCatalogItemsGrouping

    // console.log({level, hierarchy, isCatalogItemDropdown})
    return isCatalogItemDropdown
  }

  // endregion

  // region Local Hierarchy State

  @observable hierarchy = null

  @computed get hierarchyGroup() {
    const {props: {level}, groupBy} = this
    if (!_.isNumber(level)) return null
    return groupBy.hierarchyGroups[level]
  }

  // endregion

  @computed get isProductView() { return this.grouping === this.groupBy.catalogItemsGroup}

  private _debouncedUpdateHierarchyInGlobalStore = _.debounce(() => {
    this.groupBy.hierarchy = this.hierarchy.slice()
  }, 500)

  /**
   * We don't want to query all of our counts and render giant lists during our initial load, but neither do we want to wait for the user to have actually clicked a menu once
   */
  @observable popoverHasOpened = false

  @computed get groupNameCount() {
    const {catalog: {filter: {state: {groups}}}} = this
    return (groups && groups.vlpVersions) ? groups.vlpVersions.selected.size > 0 ? `(${groups.vlpVersions.selected.size})` : groups.vlpVersions.groupedByValue ? `(${Object.keys((groups.vlpVersions.groupedByValue))?.length})` : '(...)' :
           '(...)'
  }
}

export const GroupByDropdownStoreContext = React.createContext<GroupByDropdownStore>(undefined)
export const useGroupByDropdownStore = () => useContext(GroupByDropdownStoreContext)

/// endregion

export const GroupByDropdownContentPanel = observer<{ style?: React.CSSProperties }>(function GroupByDropdownContentPanel({style}) {
  const store = useGroupByDropdownStore()

  if (!store) return null

  const {popoverHasOpened} = store,
    {
      strings, catalog: {
      catalogItemName, workspaceConfig, sectionsForGroupBy: sections, groupBy, labelForSection, catalogConfig, sectionsWithDynamicFields,
      groupBy: {
        depth, hierarchy, menuStore, maxGroupDepth,
        menuStore: {specOptionMenuItems, hierarchyOptionMenuItems, isSearching, selectedSection, noResults, searchText}
      }
    }
    } = store

  //console.log(store)

  return (
    <Menu className={css.panelRoot}
          data-searching={isSearching}
          data-orientation={catalogConfig?.features?.groupBy?.dropdown?.columns ? 'columns' : 'rows'}
          style={{...(style ?? {}), '--columns': catalogConfig?.features?.groupBy?.dropdown?.columns, '--rows': catalogConfig?.features?.groupBy?.dropdown?.rows} as any}>
      {!popoverHasOpened ? null : _.isEmpty(groupBy.counts)
                                  ? <LoadingIndicator className={css.loading}/>
                                  : <div className={css.panelGrid}>
                                    <Navbar className={css.topToolbar} style={{gridArea: 'toolbar'}}>
                                      <NavbarGroup align='left'>
                                        <ButtonGroup className={css.sectionButtons}>
                                          {sections.length > 1
                                           ? <Button
                                             minimal
                                             active={selectedSection == 'all'}
                                             onClick={() => menuStore.selectedSection = 'all'}
                                             text={`${_.capitalize(strings.all)}`}
                                             data-testid={`groupBy-sectionBtn-all`}/>
                                           : <Button minimal text={catalogItemName}/>}

                                          {sections.length > 1 && sections.map((section, i) => {
                                            //const fieldsInHierarchy = fieldBySection[section]?.filter(f => hierarchy.find(v => v == f.field)) ?? []
                                            //console.log({section, i, label: labelForSection(section)})
                                            return <Button key={i}
                                                           minimal
                                                           active={selectedSection == section}
                                                           onClick={() => menuStore.selectedSection = section}
                                                           text={labelForSection(section)}
                                                           data-section={section}
                                                           data-testid={`groupBy-sectionBtn-${section}`}/>
                                          })}
                                        </ButtonGroup>
                                        <TitleSubtitleButtons/>
                                      </NavbarGroup>
                                      <NavbarGroup align='right' className={css.headerRight}>
                                        <SearchBar/>
                                      </NavbarGroup>
                                    </Navbar>
                                    <div style={{gridArea: 'fields-left'}}>
                                      <Menu data-menu='all'>
                                        <CatalogItemGroupByMenuItem/>

                                        {/* YFTS-5618 - won't do */}
                                        {/*<GroupByMenuItem name={strings.offlineItems} count={2} value={OFFLINE_ID} />*/}
                                        {hierarchyOptionMenuItems?.length ? <Fragment>
                                          <MenuDivider title={strings.hierarchy}/>
                                          {hierarchyOptionMenuItems}
                                        </Fragment> : undefined}

                                        {specOptionMenuItems?.length ? <Fragment>
                                          <MenuDivider title={strings.specifications}/>
                                          {specOptionMenuItems}
                                        </Fragment> : undefined}
                                      </Menu>
                                    </div>


                                    <div className={css.fieldContents} style={{gridArea: 'fields-right'}}>
                                      {noResults ? <div className={css.noResults}>{strings.noResults} found for '{searchText}'</div> :
                                       <Fragment>
                                         <GroupMenuItems includeWrapper section={selectedSection}/>
                                         {(selectedSection === workspaceConfig._id) && sectionsWithDynamicFields.map((section, i) => <GroupMenuItems key={i} section={section.id} includeLabel/>)}
                                       </Fragment>}
                                    </div>

                                    <div className={css.footer} style={{gridArea: 'footer'}}>
                                      <div className={css.footerLeft}>
                                        <GroupByToolbarItems_MenuFooter/>
                                      </div>

                                      <div className={css.footerRight}>
                                        <div className={css.depth}>
                                          <TitleSubtitleButtons/>

                                          <label>Depth:</label>
                                          <ButtonGroup>
                                            {_.range(0, maxGroupDepth + 1).map(g =>
                                              <Button text={g} minimal
                                                      key={g} active={depth == g} onClick={groupBy.onSetDepth(g)}
                                                      data-testid={`groupBy-depthBtn-${g}`}/>)}
                                            <Tooltip2 content={strings.clearGroups} placement='bottom'>
                                              <PrimaryIconButton
                                                wrapperClassName={css.clearGroupBy}
                                                icon={<Icon icon='clean'/>}
                                                disabled={hierarchy.length === 0}
                                                onClick={groupBy.clearHierarchy}/>
                                            </Tooltip2>
                                          </ButtonGroup>
                                        </div>
                                      </div>
                                    </div>
                                  </div>}
    </Menu>
  )
})

type GroupMenuItemsStoreProps = {
  section?: string
}

export class GroupMenuItemsStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: GroupMenuItemsStoreProps) {
    super(stores)
  }

  @computed get filteredFields() {
    const {groupBy: {groupByFields, catalog: {hasSection}, menuStore: {hierarchyGroups}}, catalog, props: {section}} = this

    return groupByFields.filter(f => (
      f.group?.hideGroupByColumn !== true && !hierarchyGroups.some(hg => hg.groupName === f.field)
      && ((f.group !== false || f.group?.spec === false || f.def || (f.field.indexOf('attributes.') != -1)) && f?.group?.customAttr !== false) && hasSection({fieldName: f.field, section}))
    )
  }

  @computed get menuItemBySection() {
    return _.groupBy(this.filteredFields, f => f.section ?? this.catalog.workspaceConfig._id)
  }

  @computed get sectionConfig() {
    return this.catalog.sectionConfigFor(this.props.section)
  }

  @computed get sectionDynamicFieldName() {
    const {sectionConfig} = this
    const sectionDynamicFieldName = sectionConfig?.viewOptions?.field ? `${sectionConfig.path}.${sectionConfig.viewOptions.field}` : null

    return sectionDynamicFieldName
  }

  @computed get menuItems() {
    const {groupBy, filteredFields, sectionDynamicFieldName} = this
    return _.orderBy(filteredFields, f => sectionDynamicFieldName === f.field ? ' ' : (f.label ?? f?.def?.displayName?.singular))
      .map(f => <GroupByMenuItem grouping={groupBy.groupingByName.get(f.field)} key={f.field}/>)
      .filter(_.identity)
  }
}

export const GroupMenuItemsStoreContext = React.createContext<GroupMenuItemsStore>(null)
export const useGroupMenuItemsStore = () => React.useContext(GroupMenuItemsStoreContext)

export const GroupMenuItems = observer<{ section?: string, includeWrapper?: boolean, includeLabel?: boolean }>(function GroupMenuItems({section, includeWrapper, includeLabel}) {
  const obsProps = useAsObservableSource({section}),
    store = useVipLocalStore((ws) => new GroupMenuItemsStore(ws, obsProps)),
    {menuItems, sectionConfig} = store
  if (_.isEmpty(menuItems)) return null

  // console.log(menuItemBySection)

  return <GroupMenuItemsStoreContext.Provider value={store}>
    {includeLabel || includeWrapper ? <Fragment>
                                      {includeLabel && <MenuDivider
		                                  data-id={sectionConfig.id}
		                                  data-field={sectionConfig.field}
		                                  data-path={sectionConfig.path}
		                                  title={`${pluralize(sectionConfig.label ?? sectionConfig.id, 1)} Attributes`}/>}
                                      <div className={css.fieldGrid} style={{'--items': menuItems.length} as any}>
                                        <GroupMenuItemsBySection/>
                                      </div>
                                    </Fragment>
                                    : menuItems}
  </GroupMenuItemsStoreContext.Provider>
})

const GroupMenuItemsBySection = observer(function GroupMenuItemsBySection() {
  const {strings, catalog, groupBy, menuItemBySection, sectionDynamicFieldName} = useGroupMenuItemsStore(),
    {sectionById} = catalog,
    keys = _.orderBy(_.keys(menuItemBySection), k => k == catalog.workspace.id ? ' ' : sectionById[k]?.label),
    {menuStore: {searchWords}} = groupBy

  return (<Fragment>
    {keys.filter(k => sectionById[k]).map(k => {
      const section = sectionById[k],
        ws = section?.workspace ?? catalog.workspaceConfig

      if (section?.field) {
        const f = catalog._fieldByName[section.field]
        if (f?.group?.hideGroupByColumn) {
          return null
        }
      }

      const fields = _.orderBy(menuItemBySection[k], f => sectionDynamicFieldName === f.field ? ' ' : f.esIndex ? ' ' : catalog.labelForField(f))

      return <div className={css.groupMenuItemByWorkspace} key={k} data-workspace={ws._id} data-section={k}>
        <MenuDivider title={<Highlighter textToHighlight={section?.label ?? ws?.itemName ?? strings.emptyLabel} searchWords={searchWords}/>}/>
        {fields.map(f => <GroupByMenuItem grouping={groupBy.groupingByName.get(f.field)} key={f.field} fixedmaxwidth={(keys.length == 1).toString()}/>)
          .filter(_.identity)}
      </div>
    }).filter(_.identity)}
  </Fragment>)
})

const SearchBar = observer<{ className?: string }>(function SearchBar({className}) {
  const {groupBy: {menuStore: {searchText, onSearch}}} = useGroupByDropdownStore()
  return (<div className={classNames(css.searcher, className)}>
    <SearchField2 active autoFocus={false} onSearch={onSearch} value={searchText}/>
  </div>)
})

export const CatalogItemGroupByMenuItem = observer(function CatalogItemGroupByMenuItem(props) {
  const {groupBy: {catalogItemsGroup}} = useGroupByDropdownStore()

  return <Fragment>
    <GroupByMenuItem grouping={catalogItemsGroup} label={'None'} {...props}/>
  </Fragment>
})

const TitleSubtitleButtons = observer(function TitleSubtitleButtons() {
  const {groupBy: {isProductView, menuStore: {subtitleButtonActive, onToggleSubtitleButton}}, isCatalogItemDropdown, props: {group}} = useGroupByDropdownStore()

  if (isProductView) return null

  return (
    <div className={css.titleSubtitleButtons}>
      <PrimaryIconButton tooltip={'Group Heading'} icon={<span>H</span>} active={!subtitleButtonActive} onClick={onToggleSubtitleButton}/>
      <PrimaryIconButton tooltip={`${group ? group.groupTitle : 'Group'} Subtitles`} icon={<span>S</span>} disabled={isCatalogItemDropdown} active={subtitleButtonActive} onClick={onToggleSubtitleButton}/>
    </div>
  )
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Menus/Toolbar/GroupChips/GroupByDropdown.tsx